import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import type { RoleResponse, Paginated } from '../../../api/types';
import { rolesKeys } from './keys';

interface RolesListParams extends Record<string, unknown> {
  limit?: number;
  offset?: number;
  search?: string;
  isActive?: boolean;
}

export interface RolesListOptions {
  enabled?: boolean;
}

export function useRolesList(params: RolesListParams = {}, options: RolesListOptions = {}) {
  return useQuery<Paginated<RoleResponse>>({
    queryKey: rolesKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<RoleResponse>>('/admin/roles', { params });
      return data;
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useRole(id: string) {
  return useQuery<RoleResponse>({
    queryKey: rolesKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<RoleResponse>(`/admin/roles/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

interface CreateRoleInput {
  name: string;
  description?: string;
  isActive?: boolean;
  permissionIds?: string[];
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation<RoleResponse, Error, CreateRoleInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post<RoleResponse>('/admin/roles', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}

interface UpdateRoleInput {
  id: string;
  body: {
    name?: string;
    description?: string;
    isActive?: boolean;
    permissionIds?: string[];
  };
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation<RoleResponse, Error, UpdateRoleInput>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.patch<RoleResponse>(`/admin/roles/${id}`, body);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/admin/roles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.lists() });
    },
  });
}
